import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieMonster from "../../tools/CookieMonster";
import { BASIC_URL } from "../basicurl";
import {
  ASafetyCheck,
  ASafetyCheckWithAnswer,
  ASubmitChecklistResponse,
  ASubmitResultChecklistResponse,
} from "../../types/safetycheck";
import {
  AVehicle,
  AVehicleWithColor,
  AVehicleWithSafetyCheck,
} from "../../types/vehicle";
import {
  APaginationEntity,
  BackendDataPageShape,
  BackendDataShape,
  BackendPaginationRequestObject,
} from "../../types";
import { convertToEncodedURL } from "../../tools";
import { chipColors } from "../../assets/theme";

export const checklistStoreAPI = createApi({
  reducerPath: "checklistStoreAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASIC_URL}`,
    prepareHeaders: (headers) => {
      const cookie = new CookieMonster();
      let token = cookie.loadCookie("jwt");
      headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: [
    "Vehicle",
    "SafetyCheck",
    "SubmitSafetyCheck",
    "VehicleWithSafetyCheck",
  ],
  endpoints: (builder) => {
    return {
      getSafetyCheckByType: builder.query<
        { title: string; list: ASafetyCheck[] }[],
        string
      >({
        query: (type) => {
          return {
            url: `/safetycheck/${type}`,
            method: "GET",
          };
        },
        providesTags: (_, __, type) => [{ type: "SafetyCheck", id: type }],
        transformResponse: (res: BackendDataShape<ASafetyCheck[]>) => {
          const temp: {
            [title: string]: ASafetyCheck[];
          } = {};
          const data = res.data.slice();
          data.forEach((d) => {
            if (!temp[d.category]) {
              temp[d.category] = [];
            }
            const existingObject = temp[d.category].find(
              (item) => item.seq === d.seq
            );
            if (!existingObject) {
              temp[d.category].push(d);
            }
          });
          let dataMap = Object.keys(temp).map((k) => {
            return {
              title: k,
              list: temp[k],
            };
          });
          return dataMap;
        },
      }),

      getVehicles: builder.query<
        APaginationEntity<AVehicleWithColor[]>,
        BackendPaginationRequestObject<
          Partial<Omit<AVehicleWithColor, "day_left"> & { dayLeft: number }>
        >
      >({
        query: (obj) => {
          let params = "";
          if (obj) {
            params = convertToEncodedURL(obj);
            params = params.length ? "?" + params : "";
          }
          return {
            url: `/vehicle` + params,
            method: "GET",
          };
        },
        providesTags: ["Vehicle"],

        transformResponse: (
          response: BackendDataPageShape<AVehicleWithColor[]>
        ) => {
          const { meta, entities } = response.data;
          const dataWithChip = entities.map((val, idx) => {
            return {
              ...val,
              color: chipColors[idx % chipColors.length],
            };
          });
          return { meta, entities: dataWithChip };
        },
      }),
      getVehicleById: builder.query<AVehicle, string>({
        query: (id) => {
          return {
            url: `/vehicle/${id}`,
            method: "GET",
          };
        },
        providesTags: (_, __, id) => [{ type: "Vehicle", id }],
        transformResponse: (res: BackendDataShape<AVehicle>) => {
          return res.data;
        },
      }),
      getVehicleWithSafetyCheckById: builder.query<
        AVehicleWithSafetyCheck,
        Partial<AVehicleWithSafetyCheck>
      >({
        query: ({ internal_id }) => {
          return {
            url: `/vehicle/${internal_id}/safetycheck/latest/inactive`,
            method: "GET",
          };
        },
        providesTags: (_, __, { internal_id }) => [
          { type: "VehicleWithSafetyCheck", id: internal_id },
        ],
        transformResponse: (res: BackendDataShape<AVehicleWithSafetyCheck>) => {
          return res.data;
        },
      }),
      getSubmittedChecklistById: builder.query<
        ASubmitResultChecklistResponse,
        string
      >({
        query: (id) => {
          return {
            url: `/safetycheck/result/${id}`,
            method: "GET",
          };
        },
        providesTags: (_, __, id) => [{ type: "SubmitSafetyCheck", id }],
        transformResponse: (
          res: BackendDataShape<ASubmitResultChecklistResponse>
        ) => {
          return res.data;
        },
      }),
      submitChecklist: builder.mutation<
        ASubmitChecklistResponse,
        { data: ASafetyCheckWithAnswer[]; vehicleId: string }
      >({
        query: ({ data, vehicleId }) => {
          return {
            url: `/safetycheck/submit/${vehicleId}`,
            method: "POST",
            body: data,
          };
        },
        invalidatesTags: ["Vehicle"],
        transformResponse: (res: ASubmitChecklistResponse) => res,
      }),
    };
  },
});

export const {
  useGetSafetyCheckByTypeQuery,
  useGetVehicleByIdQuery,
  useGetVehiclesQuery,
  useGetSubmittedChecklistByIdQuery,
  useGetVehicleWithSafetyCheckByIdQuery,
  useSubmitChecklistMutation,
  util: { resetApiState: resetChecklistStore },
} = checklistStoreAPI;
